import { z } from 'zod';
import {
  ACTUAL_MODALITY_VALUES,
  APPROVAL_REQUEST_STATUS_VALUES,
  CALENDAR_EVENT_TYPE_VALUES,
  COMPENSATION_CATEGORY_VALUES,
  CORRECTION_REASON_VALUES,
  DAILY_SEGMENT_VALUES,
  LEAVE_ACCRUAL_VALUES,
  LEAVE_ATTACHMENT_RULE_VALUES,
  LEAVE_BALANCE_CHECK_VALUES,
  LEAVE_TYPE_VALUES,
  MAKEUP_STATUS_VALUES,
  ONLINE_PLATFORM_VALUES,
  OVERLOAD_ASSIGNMENT_TYPE_VALUES,
  PROOF_TYPE_VALUES,
  REQUEST_STATUS_VALUES,
  SCHEDULED_MODALITY_VALUES,
  SCOPE_TYPE_VALUES,
  SUBSTITUTE_COMPENSATION_POLICY_VALUES,
  UPLOAD_FILE_TYPE_VALUES,
  WORK_ARRANGEMENT_VALUES,
  WORKFLOW_APPROVER_PERMISSIONS,
  WORKFLOW_CODE_VALUES,
  WORKFLOW_STEP_CONDITION_VALUES,
} from '@fas/types';
import {
  booleanQuerySchema,
  codeSchema,
  isoDateSchema,
  nonEmptyTrimmed,
  optionalText,
  paginationQuerySchema,
  timeOfDaySchema,
  uuidSchema,
} from './common';

/*
 * Academic exceptions (Phase 1D): files and class proof, session progress, approval
 * workflows, corrections, makeup, substitution, overload, duties, leave and calendar.
 */

const explanation = (label = 'Reason') =>
  z
    .string(`${label} is required`)
    .trim()
    .min(5, `${label} must be at least 5 characters`)
    .max(1000, `${label} must be at most 1000 characters`);

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .transform((v) => (v.length === 0 ? null : v))
  .pipe(z.url({ protocol: /^https$/, error: 'Meeting link must be an https:// URL' }).nullable())
  .nullable()
  .optional();

const days = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 86_400_000;

function refineDateRange(v: { from?: string; to?: string }, ctx: z.RefinementCtx, maxDays: number, keys: [string, string] = ['from', 'to']) {
  if (!v.from || !v.to) return;
  if (v.to < v.from) ctx.addIssue({ code: 'custom', path: [keys[1]], message: 'Must be on or after the start date' });
  else if (days(v.from, v.to) > maxDays) ctx.addIssue({ code: 'custom', path: [keys[1]], message: `The range cannot exceed ${maxDays} days` });
}

function refineTimes(v: { startTime?: string | null; endTime?: string | null }, ctx: z.RefinementCtx, path = 'endTime') {
  if (v.startTime && v.endTime && v.startTime === v.endTime) ctx.addIssue({ code: 'custom', path: [path], message: 'End time must differ from start time' });
}

// ── Decisions shared by every request type (§64) ───────────────────────────────
const conflictAck = {
  /** Approve despite blocking schedule conflicts (requires a reason; audited). */
  acknowledgeConflicts: z.boolean().optional(),
  conflictReason: z.string().trim().max(500).optional(),
};

function refineAck(v: { acknowledgeConflicts?: boolean; conflictReason?: string }, ctx: z.RefinementCtx) {
  if (v.acknowledgeConflicts && (!v.conflictReason || v.conflictReason.length < 5)) {
    ctx.addIssue({ code: 'custom', path: ['conflictReason'], message: 'Give a reason (at least 5 characters) to override conflicts' });
  }
}

export const approveRequestSchema = z.object({ note: optionalText(1000), ...conflictAck }).superRefine(refineAck);
export type ApproveRequestInput = z.infer<typeof approveRequestSchema>;

export const rejectRequestSchema = z.object({ note: z.string('Explain the decision').trim().min(3, 'Explain the decision (at least 3 characters)').max(1000) });
export type RejectRequestInput = z.infer<typeof rejectRequestSchema>;

export const cancelRequestSchema = z.object({ reason: optionalText(500) });
export type CancelRequestInput = z.infer<typeof cancelRequestSchema>;

const requestListBase = paginationQuerySchema.extend({
  facultyId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Only requests about or filed by the caller. */
  mine: booleanQuerySchema.optional(),
});

export const listRequestsQuerySchema = requestListBase.extend({ status: z.enum(REQUEST_STATUS_VALUES).optional() }).superRefine((v, ctx) => refineDateRange(v, ctx, 370));
export type ListRequestsQuery = z.infer<typeof listRequestsQuerySchema>;

export const listMakeupQuerySchema = requestListBase.extend({ status: z.enum(MAKEUP_STATUS_VALUES).optional() }).superRefine((v, ctx) => refineDateRange(v, ctx, 370));
export type ListMakeupQuery = z.infer<typeof listMakeupQuerySchema>;

// ── Files (§16, §52) ───────────────────────────────────────────────────────────
export const attachmentTargetSchema = z.object({
  entityType: z.string().trim().min(1).max(60),
  entityId: uuidSchema,
});
export type AttachmentTarget = z.infer<typeof attachmentTargetSchema>;

/** Signed download link parameters (§52: no raw storage URLs, short-lived access). */
export const fileDownloadQuerySchema = z.object({
  exp: z.coerce.number().int().positive(),
  u: uuidSchema,
  inline: z.enum(['0', '1']).default('0'),
  sig: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Invalid signature'),
});
export type FileDownloadQuery = z.infer<typeof fileDownloadQuerySchema>;

export const fileAccessUrlSchema = z.object({ inline: z.boolean().default(false) });
export type FileAccessUrlInput = z.infer<typeof fileAccessUrlSchema>;

export const uploadAllowedTypesSchema = z
  .array(z.enum(UPLOAD_FILE_TYPE_VALUES))
  .min(1, 'Allow at least one file type')
  .refine((v) => new Set(v).size === v.length, 'File types must not repeat');

// ── Class proof and session progress (§16, §17) ────────────────────────────────
export const uploadProofFieldsSchema = z.object({
  proofType: z.enum(PROOF_TYPE_VALUES),
  description: optionalText(1000),
});
export type UploadProofFields = z.infer<typeof uploadProofFieldsSchema>;

export const reviewProofSchema = z
  .object({ decision: z.enum(['ACCEPTED', 'REJECTED']), note: optionalText(1000) })
  .superRefine((v, ctx) => {
    if (v.decision === 'REJECTED' && !v.note) ctx.addIssue({ code: 'custom', path: ['note'], message: 'Explain why the proof is rejected' });
  });
export type ReviewProofInput = z.infer<typeof reviewProofSchema>;

export const sessionProgressSchema = z.object({
  topicCovered: nonEmptyTrimmed(500, 'Topic covered'),
  syllabusUnit: optionalText(100),
  learningOutcomes: optionalText(2000),
  activityConducted: optionalText(2000),
  assessmentAdministered: optionalText(1000),
  participationNotes: optionalText(2000),
  remarks: optionalText(1000),
  /** Version being edited (optimistic concurrency); omit when recording for the first time. */
  version: z.number().int().min(1).optional(),
});
export type SessionProgressInput = z.infer<typeof sessionProgressSchema>;

// ── Approval workflows (§64) ───────────────────────────────────────────────────
const SCOPE_RANK: Record<(typeof SCOPE_TYPE_VALUES)[number], number> = { DEPARTMENT: 1, COLLEGE: 2, INSTITUTION: 3 };

const hours = z.number().int('Use whole hours').min(1, 'At least 1 hour').max(720, 'At most 720 hours (30 days)');

export const workflowStepSchema = z
  .object({
    name: nonEmptyTrimmed(100, 'Step name'),
    permission: z.enum(WORKFLOW_APPROVER_PERMISSIONS),
    minScope: z.enum(SCOPE_TYPE_VALUES),
    maxScope: z.enum(SCOPE_TYPE_VALUES),
    condition: z.enum(WORKFLOW_STEP_CONDITION_VALUES),
    enabled: z.boolean(),
    /** Only requests at least this long: working days (leave) or minutes (other types) (ADR-051). */
    minDuration: z
      .number()
      .positive('Must be more than 0')
      .max(100_000)
      .refine((v) => Number.isInteger(v * 2), 'Use whole or half units')
      .nullable()
      .default(null),
    /** Only requests about faculty of these employment types; empty = any. */
    employmentTypeCodes: z
      .array(z.string().trim().toUpperCase().min(1).max(30))
      .max(10, 'At most 10 employment types')
      .refine((v) => new Set(v).size === v.length, 'Employment types must not repeat')
      .default([]),
    /** Hours pending before the approvers are reminded (ADR-052). */
    remindAfterHours: hours.nullable().default(null),
    /** Hours pending before the step widens to the next scope level. */
    escalateAfterHours: hours.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (SCOPE_RANK[v.minScope] > SCOPE_RANK[v.maxScope]) {
      ctx.addIssue({ code: 'custom', path: ['maxScope'], message: 'The broadest scope must be at least the narrowest scope' });
    }
    if (v.remindAfterHours !== null && v.escalateAfterHours !== null && v.escalateAfterHours <= v.remindAfterHours) {
      ctx.addIssue({ code: 'custom', path: ['escalateAfterHours'], message: 'Escalate later than the reminder' });
    }
  });
export type WorkflowStepInput = z.infer<typeof workflowStepSchema>;

export const updateWorkflowSchema = z
  .object({
    name: nonEmptyTrimmed(150, 'Name').optional(),
    description: optionalText(1000),
    autoApproveEligibleRequester: z.boolean().optional(),
    steps: z.array(workflowStepSchema).min(1, 'Add at least one step').max(6, 'At most 6 steps'),
  })
  .superRefine((v, ctx) => {
    // Some step must apply to every request, or requests outside all criteria would go unapproved (ADR-051).
    if (!v.steps.some((s) => s.enabled && s.condition === 'ALWAYS' && s.minDuration === null && s.employmentTypeCodes.length === 0)) {
      ctx.addIssue({ code: 'custom', path: ['steps'], message: 'At least one enabled step must always apply (without conditions)' });
    }
  });
export type UpdateWorkflowInput = z.infer<typeof updateWorkflowSchema>;

export const listApprovalsQuerySchema = paginationQuerySchema.extend({
  workflowCode: z.enum(WORKFLOW_CODE_VALUES).optional(),
  status: z.enum(APPROVAL_REQUEST_STATUS_VALUES).optional(),
  /** actionable: waiting for the caller's decision · requested: filed by the caller · decided: decided by the caller. */
  view: z.enum(['actionable', 'requested', 'decided']).default('actionable'),
});
export type ListApprovalsQuery = z.infer<typeof listApprovalsQuerySchema>;

// ── Corrections (§25, §40.6) ───────────────────────────────────────────────────
const instant = z.iso.datetime({ offset: true, error: 'Must be a date and time' });

export const createCorrectionSchema = z
  .object({
    reasonCode: z.enum(CORRECTION_REASON_VALUES),
    reasonText: explanation('Explanation'),
    proposed: z
      .object({
        actualClockIn: instant.optional(),
        actualClockOut: instant.optional(),
        actualModality: z.enum(ACTUAL_MODALITY_VALUES).optional(),
        workArrangement: z.enum(WORK_ARRANGEMENT_VALUES).optional(),
      })
      .strict(),
  })
  .superRefine((v, ctx) => {
    const p = v.proposed;
    if (!p.actualClockIn && !p.actualClockOut && !p.actualModality && !p.workArrangement) {
      ctx.addIssue({ code: 'custom', path: ['proposed'], message: 'Propose at least one corrected value' });
    }
    if (p.actualClockIn && p.actualClockOut && Date.parse(p.actualClockOut) <= Date.parse(p.actualClockIn)) {
      ctx.addIssue({ code: 'custom', path: ['proposed', 'actualClockOut'], message: 'Clock-out must be after clock-in' });
    }
  });
export type CreateCorrectionInput = z.infer<typeof createCorrectionSchema>;

export const listCorrectionsQuerySchema = requestListBase
  .extend({ status: z.enum(REQUEST_STATUS_VALUES).optional(), sessionId: uuidSchema.optional() })
  .superRefine((v, ctx) => refineDateRange(v, ctx, 370));
export type ListCorrectionsQuery = z.infer<typeof listCorrectionsQuerySchema>;

// ── Makeup (§18) ───────────────────────────────────────────────────────────────
const makeupFields = {
  originalScheduleId: uuidSchema,
  originalDate: isoDateSchema,
  reason: explanation(),
  proposedDate: isoDateSchema,
  proposedStartTime: timeOfDaySchema,
  proposedEndTime: timeOfDaySchema,
  proposedLocationId: uuidSchema.nullable().optional(),
  proposedModality: z.enum(SCHEDULED_MODALITY_VALUES),
  onlinePlatform: z.enum(ONLINE_PLATFORM_VALUES).nullable().optional(),
  meetingUrl: httpsUrl,
  remarks: optionalText(1000),
};

const refineMakeup = (v: { proposedStartTime?: string; proposedEndTime?: string }, ctx: z.RefinementCtx) =>
  refineTimes({ startTime: v.proposedStartTime, endTime: v.proposedEndTime }, ctx, 'proposedEndTime');

export const createMakeupSchema = z
  .object({ ...makeupFields, /** false keeps the request as a draft (§18.2 DRAFT). */ submit: z.boolean().default(true) })
  .superRefine(refineMakeup);
export type CreateMakeupInput = z.infer<typeof createMakeupSchema>;

/** Drafts only; the original meeting cannot change (create a new request instead). */
export const updateMakeupSchema = z
  .object(makeupFields)
  .omit({ originalScheduleId: true, originalDate: true })
  .partial()
  .superRefine(refineMakeup);
export type UpdateMakeupInput = z.infer<typeof updateMakeupSchema>;

export const makeupCandidatesQuerySchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type MakeupCandidatesQuery = z.infer<typeof makeupCandidatesQuerySchema>;

// ── Substitution (§19) ─────────────────────────────────────────────────────────
export const createSubstitutionSchema = z.object({
  scheduleId: uuidSchema,
  substitutionDate: isoDateSchema,
  substituteFacultyId: uuidSchema,
  reason: explanation(),
  compensationPolicy: z.enum(SUBSTITUTE_COMPENSATION_POLICY_VALUES).default('SUBSTITUTE_PAYABLE'),
});
export type CreateSubstitutionInput = z.infer<typeof createSubstitutionSchema>;

export const approveSubstitutionSchema = z
  .object({ note: optionalText(1000), compensationPolicy: z.enum(SUBSTITUTE_COMPENSATION_POLICY_VALUES).optional(), ...conflictAck })
  .superRefine(refineAck);
export type ApproveSubstitutionInput = z.infer<typeof approveSubstitutionSchema>;

export const substituteCandidatesQuerySchema = z.object({
  scheduleId: uuidSchema,
  date: isoDateSchema,
  q: z.string().trim().max(100).optional(),
});
export type SubstituteCandidatesQuery = z.infer<typeof substituteCandidatesQuerySchema>;

// ── Overload / extra load (§20) ────────────────────────────────────────────────
export const createOverloadSchema = z
  .object({
    /** Defaults to the caller's own faculty profile. */
    facultyId: uuidSchema.optional(),
    loadType: z.enum(['OVERLOAD', 'EXTRA_LOAD'] as const),
    assignmentType: z.enum(OVERLOAD_ASSIGNMENT_TYPE_VALUES),
    assignmentDate: isoDateSchema,
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    courseSectionId: uuidSchema.nullable().optional(),
    locationId: uuidSchema.nullable().optional(),
    scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES).default('FACE_TO_FACE'),
    compensationCategory: z.enum(COMPENSATION_CATEGORY_VALUES),
    requiresClocking: z.boolean().default(true),
    reason: explanation(),
    remarks: optionalText(1000),
  })
  .superRefine((v, ctx) => refineTimes(v, ctx));
export type CreateOverloadInput = z.infer<typeof createOverloadSchema>;

// ── Administrative duties (§22) ────────────────────────────────────────────────
export const createDutyCategorySchema = z.object({
  code: codeSchema(40),
  name: nonEmptyTrimmed(100, 'Name'),
  description: optionalText(500),
  requiresClocking: z.boolean().default(false),
  requiresApproval: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1000).default(100),
});
export type CreateDutyCategoryInput = z.infer<typeof createDutyCategorySchema>;

export const updateDutyCategorySchema = z.object({
  name: nonEmptyTrimmed(100, 'Name').optional(),
  description: optionalText(500),
  requiresClocking: z.boolean().optional(),
  requiresApproval: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
  active: z.boolean().optional(),
});
export type UpdateDutyCategoryInput = z.infer<typeof updateDutyCategorySchema>;

export const createDutySchema = z
  .object({
    /** Defaults to the caller's own faculty profile; another faculty member needs duty.manage. */
    facultyId: uuidSchema.optional(),
    categoryId: uuidSchema,
    title: nonEmptyTrimmed(200, 'Title'),
    description: optionalText(2000),
    dutyDate: isoDateSchema,
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    locationId: uuidSchema.nullable().optional(),
    scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES).default('FACE_TO_FACE'),
    /** Defaults to the category setting (§22 "may optionally require clock-in/out"). */
    requiresClocking: z.boolean().optional(),
  })
  .superRefine((v, ctx) => refineTimes(v, ctx));
export type CreateDutyInput = z.infer<typeof createDutySchema>;

// ── Leave (§23) ────────────────────────────────────────────────────────────────
export const createLeaveSchema = z
  .object({
    /** Defaults to the caller; filing for another faculty member needs leave.manage. */
    facultyId: uuidSchema.optional(),
    leaveType: z.enum(LEAVE_TYPE_VALUES),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    portion: z.enum(DAILY_SEGMENT_VALUES).default('FULL_DAY'),
    hours: z.number().min(0.5).max(2000).nullable().optional(),
    reason: explanation(),
  })
  .superRefine((v, ctx) => {
    refineDateRange({ from: v.startDate, to: v.endDate }, ctx, 366, ['startDate', 'endDate']);
    if (v.portion !== 'FULL_DAY' && v.startDate !== v.endDate) {
      ctx.addIssue({ code: 'custom', path: ['portion'], message: 'A half-day leave covers a single date' });
    }
    // Balances are kept per calendar year (ADR-049).
    if (v.startDate.slice(0, 4) !== v.endDate.slice(0, 4)) {
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Leave counts against one calendar year; file the days of each year separately' });
    }
  });
export type CreateLeaveInput = z.infer<typeof createLeaveSchema>;

/** Returning early from approved leave that has started (ADR-050). */
export const shortenLeaveSchema = z
  .object({
    /** First date back at work; the leave then ends the day before. */
    backOn: isoDateSchema,
    reason: explanation(),
  })
  .strict();
export type ShortenLeaveInput = z.infer<typeof shortenLeaveSchema>;

// ── Leave policies and balances (Phase 2F, ADR-049) ─────────────────────────────
const halfDays = (min: number, max: number) =>
  z
    .number()
    .min(min)
    .max(max)
    .refine((v) => Number.isInteger(v * 2), 'Use whole or half days');

const leavePolicyShape = {
  leaveType: z.enum(LEAVE_TYPE_VALUES),
  /** Null: every employment type without a policy of its own. */
  employmentTypeCode: z.string().trim().toUpperCase().min(1).max(30).nullable().optional(),
  /** Days per year; null = not tracked. */
  annualDays: halfDays(0, 366).nullable(),
  accrual: z.enum(LEAVE_ACCRUAL_VALUES),
  carryOverMaxDays: halfDays(0, 366),
  balanceCheck: z.enum(LEAVE_BALANCE_CHECK_VALUES),
  attachmentRule: z.enum(LEAVE_ATTACHMENT_RULE_VALUES),
  attachmentOverDays: halfDays(0, 366).nullable().optional(),
  minNoticeDays: z.number().int().min(0).max(90),
};

function refineLeavePolicy(v: { attachmentRule?: string; attachmentOverDays?: number | null; annualDays?: number | null; balanceCheck?: string }, ctx: z.RefinementCtx) {
  if (v.attachmentRule === 'OVER_DAYS' && (v.attachmentOverDays === null || v.attachmentOverDays === undefined)) {
    ctx.addIssue({ code: 'custom', path: ['attachmentOverDays'], message: 'Say after how many days an attachment is needed' });
  }
  if (v.annualDays === null && v.balanceCheck && v.balanceCheck !== 'NONE') {
    ctx.addIssue({ code: 'custom', path: ['balanceCheck'], message: 'Without an entitlement there is no balance to check' });
  }
}

export const createLeavePolicySchema = z
  .object({
    ...leavePolicyShape,
    accrual: leavePolicyShape.accrual.default('ANNUAL'),
    carryOverMaxDays: leavePolicyShape.carryOverMaxDays.default(0),
    balanceCheck: leavePolicyShape.balanceCheck.default('WARN'),
    attachmentRule: leavePolicyShape.attachmentRule.default('NEVER'),
    minNoticeDays: leavePolicyShape.minNoticeDays.default(0),
  })
  .strict()
  .superRefine(refineLeavePolicy);
export type CreateLeavePolicyInput = z.infer<typeof createLeavePolicySchema>;

/** The leave type and employment type are fixed; create another policy for another pair. */
export const updateLeavePolicySchema = z
  .object(leavePolicyShape)
  .omit({ leaveType: true, employmentTypeCode: true })
  .partial()
  .strict()
  .superRefine(refineLeavePolicy);
export type UpdateLeavePolicyInput = z.infer<typeof updateLeavePolicySchema>;

export const leaveAdjustmentSchema = z
  .object({
    facultyId: uuidSchema,
    leaveType: z.enum(LEAVE_TYPE_VALUES),
    year: z.number().int().min(2000).max(2100),
    /** Days added (positive) or removed (negative). */
    days: halfDays(-366, 366).refine((v) => v !== 0, 'An adjustment changes the balance'),
    reason: explanation(),
  })
  .strict();
export type LeaveAdjustmentInput = z.infer<typeof leaveAdjustmentSchema>;

export const leaveBalancesQuerySchema = paginationQuerySchema.extend({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  leaveType: z.enum(LEAVE_TYPE_VALUES).optional(),
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
});
export type LeaveBalancesQuery = z.infer<typeof leaveBalancesQuerySchema>;

export const leaveYearQuerySchema = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() });
export type LeaveYearQuery = z.infer<typeof leaveYearQuerySchema>;

export const listLeavePoliciesQuerySchema = z.object({ includeInactive: booleanQuerySchema.optional() });
export type ListLeavePoliciesQuery = z.infer<typeof listLeavePoliciesQuerySchema>;

/** The policies that apply to a faculty member (default: the caller). */
export const effectiveLeavePoliciesQuerySchema = z.object({ facultyId: uuidSchema.optional() });
export type EffectiveLeavePoliciesQuery = z.infer<typeof effectiveLeavePoliciesQuerySchema>;

export const leaveCalendarQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    departmentId: uuidSchema.optional(),
    collegeId: uuidSchema.optional(),
  })
  .superRefine((v, ctx) => refineDateRange(v, ctx, 62));
export type LeaveCalendarQuery = z.infer<typeof leaveCalendarQuerySchema>;

// ── Institutional calendar (§24) ───────────────────────────────────────────────
const calendarFields = {
  eventType: z.enum(CALENDAR_EVENT_TYPE_VALUES),
  title: nonEmptyTrimmed(200, 'Title'),
  description: optionalText(1000),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  startTime: timeOfDaySchema.nullable().optional(),
  endTime: timeOfDaySchema.nullable().optional(),
  campusId: uuidSchema.nullable().optional(),
  collegeId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  /** Defaults by event type (holidays and suspensions stop classes). */
  suspendsClasses: z.boolean().optional(),
  suspendsWork: z.boolean().optional(),
};

function refineCalendar(
  v: { startDate?: string; endDate?: string; startTime?: string | null; endTime?: string | null; campusId?: string | null; collegeId?: string | null; departmentId?: string | null },
  ctx: z.RefinementCtx,
) {
  refineDateRange({ from: v.startDate, to: v.endDate }, ctx, 366, ['startDate', 'endDate']);
  if ((v.startTime || v.endTime) && v.startDate && v.endDate && v.startDate !== v.endDate) {
    ctx.addIssue({ code: 'custom', path: ['startTime'], message: 'Times can only be set for a single-day event' });
  }
  if (v.startTime && v.endTime && v.endTime <= v.startTime) ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'End time must be after the start time' });
  if ([v.campusId, v.collegeId, v.departmentId].filter(Boolean).length > 1) {
    ctx.addIssue({ code: 'custom', path: ['departmentId'], message: 'Limit the event to one campus, college or department (or none for everyone)' });
  }
}

export const createCalendarEventSchema = z.object(calendarFields).superRefine(refineCalendar);
export type CreateCalendarEventInput = z.infer<typeof createCalendarEventSchema>;

export const updateCalendarEventSchema = z.object(calendarFields).partial().superRefine(refineCalendar);
export type UpdateCalendarEventInput = z.infer<typeof updateCalendarEventSchema>;

export const listCalendarEventsQuerySchema = paginationQuerySchema
  .extend({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    eventType: z.enum(CALENDAR_EVENT_TYPE_VALUES).optional(),
    active: booleanQuerySchema.optional(),
  })
  .superRefine((v, ctx) => refineDateRange(v, ctx, 800));
export type ListCalendarEventsQuery = z.infer<typeof listCalendarEventsQuerySchema>;

// ── Exception dashboard and reports ────────────────────────────────────────────
export const exceptionDashboardQuerySchema = z
  .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    departmentId: uuidSchema.optional(),
    collegeId: uuidSchema.optional(),
  })
  .superRefine((v, ctx) => refineDateRange(v, ctx, 92));
export type ExceptionDashboardQuery = z.infer<typeof exceptionDashboardQuerySchema>;

export const requestReportQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    departmentId: uuidSchema.optional(),
    collegeId: uuidSchema.optional(),
    facultyId: uuidSchema.optional(),
  })
  .superRefine((v, ctx) => refineDateRange(v, ctx, 370));
export type RequestReportQuery = z.infer<typeof requestReportQuerySchema>;
